import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "components/Button";
import Divider from "components/Divider/Divider";
import Text from "components/Text";
import SidebarChatItem from "aiAssitant/SidebarChatItem";
import { withTranslation } from "react-i18next";
import { useGetChatHistory } from "repositories/ai-assistant-repository";
import { debounce, uniqBy } from "lodash";
import useHandleApiError from "hooks/useHandleApiError";
import InfiniteScroll from "react-infinite-scroll-component";
import Empty from "components/Empty";
import { Spinner } from "reactstrap";
import constants from "../../constants";

const PAGE_SIZE = constants.pageSize;
function ChatSidebar({
  t,
  showSidebar,
  setShowSidebar,
  selectedOwnerId,
  fetchSessionChat,
  selectedChatId,
  setSelectedChatId,
  handleNewChat,
}) {
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [chatList, setChatList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const {
    isLoading,
    data,
    isFetching,
    error: isError,
    refetch: originalRefetch,
  } = useGetChatHistory(
    pageNumber,
    PAGE_SIZE,
    selectedOwnerId,
    searchTerm,
    showSidebar
  );

  const refetch = useCallback(async () => {
    try {
      setIsRefetching(true);
      setPageNumber(1);
      setTotalItems(0);
      setIsLoadingMore(false);
      await originalRefetch();
    } finally {
      setIsRefetching(false);
    }
  }, [originalRefetch]);

  useHandleApiError(isLoading, isFetching, isError);

  useEffect(() => {
    if (showSidebar) {
      setPageNumber(1);
      setChatList([]);
      setTotalItems(0);
      setIsLoadingMore(false);
    }
  }, [showSidebar]);

  useEffect(() => {
    setPageNumber(1);
    setChatList([]);
    setTotalItems(0);
    setIsLoadingMore(false);
  }, [searchTerm]);

  useEffect(() => {
    if (!isLoading && data && showSidebar) {
      const newItems = data?.items || [];
      if (pageNumber === 1) {
        setChatList(uniqBy(newItems, "id"));
      } else if (newItems.length > 0) {
        setChatList((prev) => uniqBy([...prev, ...newItems], "id"));
      }
      setTotalItems(data?.totalCount || 0);
      setIsLoadingMore(false);
    }
  }, [isLoading, data, pageNumber, showSidebar]);

  const handleSearchFilter = useCallback((value) => {
    setIsSearching(true);
    const trimmed = value ? value.trim() : "";
    setSearchTerm(trimmed);
  }, []);

  const optimisedSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.length >= 3 || value.length === 0) {
          const trimmedValue = value ? value.trim() : "";
          handleSearchFilter(trimmedValue);
          setTimeout(() => setIsSearching(false), 100);
        }
      }, 500),
    [handleSearchFilter]
  );

  const loadMore = useCallback(() => {
    if (!isLoadingMore && !isFetching) {
      setIsLoadingMore(true);
      setPageNumber((prev) => prev + 1);
    }
  }, [isLoadingMore, isFetching]);

  const handleRename = useCallback((chatId, newTitle) => {
    setChatList((prev) =>
      prev.map((item) =>
        item.id === chatId ? { ...item, title: newTitle } : item
      )
    );
  }, []);

  const handleDelete = useCallback((chatId) => {
    setChatList((prev) => prev.filter((item) => item.id !== chatId));
    setTotalItems((prev) => Math.max(0, prev - 1));
  }, []);

  const getSectionLabel = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (date >= startOfToday) return "Today";
    if (date >= startOfYesterday && date < startOfToday) return "Yesterday";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const sortedChatList = useMemo(() => {
    return [...chatList].sort(
      (a, b) =>
        new Date(b?.lastMessageAt || b?.createdAt || 0) -
        new Date(a?.lastMessageAt || a?.createdAt || 0)
    );
  }, [chatList]);

  const hasMore = useMemo(() => {
    return (
      sortedChatList.length < totalItems && totalItems > 0 && !isLoadingMore
    );
  }, [sortedChatList.length, totalItems, isLoadingMore]);

  if (!showSidebar) return null;

  return (
    <div className="chatbox-sidebar open">
      <div>
        <Button onClick={handleNewChat} className="w-100">
          {t("aiAssistant.newChat")}
        </Button>
        <Divider />
        <div className="search-box search-box-width">
          <input
            name="searchTerm"
            type="text"
            placeholder={t("aiAssistant.search")}
            onChange={(e) => optimisedSearch(e.target.value)}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>
      </div>

      <div
        id="chat-sidebar-scroll"
        className="chat_sidebar_content_scroll"
        style={{ position: "relative" }}
      >
        {(isSearching || isLoading) && !isRefetching && (
          <div style={{ padding: "1rem", textAlign: "center" }}>
            <Spinner animation="border" className="loader-spinner" />
          </div>
        )}
        {!isSearching && chatList?.length > 0 ? (
          <InfiniteScroll
            className="w-100"
            dataLength={sortedChatList?.length || 0}
            hasMore={hasMore}
            scrollableTarget="chat-sidebar-scroll"
            next={loadMore}
            scrollThreshold={0.95}
            loader={
              <div style={{ padding: "1rem", textAlign: "center" }}>
                <Spinner
                  animation="border"
                  size="sm"
                  className="loader-spinner"
                />
              </div>
            }
          >
            <div className="d-flex flex-column gap-sm">
              {(() => {
                let previousLabel = null;
                return sortedChatList?.map((item) => {
                  const label = getSectionLabel(
                    item?.lastMessageAt || item?.createdAt
                  );
                  const showHeader = label !== previousLabel;
                  previousLabel = label;
                  const isSelected = selectedChatId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      {showHeader && (
                        <Text
                          size="12px"
                          weight="500"
                          color="#87928D"
                          marginTop="16px"
                        >
                          {label}
                        </Text>
                      )}
                     <div className={`sidebar-chat-item-wrapper ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                      setSelectedChatId(item.id);
                      if (window.innerWidth <= 600 && typeof setShowSidebar === "function") {
                      setShowSidebar(false); 
                      }
                      }}
                      >
                        <SidebarChatItem
                          listItem={item}
                          fetchSessionChat={fetchSessionChat}
                          onRename={handleRename}
                          onDelete={handleDelete}
                        />
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </InfiniteScroll>
        ) : (
          <>
            {!isSearching && !isFetching && !isLoading && (
              <Empty Message={t("aiAssistant.noChats")} />
            )}
          </>
        )}
        {isRefetching && (
          <div className="sidebar-chat-spinner">
            <Spinner animation="border" className="loader-spinner" />
          </div>
        )}
      </div>
    </div>
  );
}
export default withTranslation()(ChatSidebar);
